import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

interface AuthState {
  user: User | null;
  setUser: (user: User | null) => void;
}

// Only the user profile is persisted (for instant render on reload); the session itself is the httpOnly cookie
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      setUser: (user) => set({ user }),
    }),
    { name: 'car-evidence-auth' },
  ),
);

export const useIsAdmin = () => useAuthStore((s) => s.user?.role === 'admin');

interface UIState {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
}));

/** Standard = sidebar + overview page; tiles = icon grid on the home page, no sidebar. */
export type ViewMode = 'standard' | 'tiles';

interface ViewModeState {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

// Remembered in this browser
export const useViewModeStore = create<ViewModeState>()(
  persist((set) => ({ viewMode: 'standard', setViewMode: (viewMode) => set({ viewMode }) }), { name: 'car-evidence-view-mode' }),
);

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  action?: ToastAction;
}

interface ToastState {
  toasts: Toast[];
  push: (type: ToastType, message: string, duration?: number, action?: ToastAction) => void;
  dismiss: (id: number) => void;
}

let toastId = 0;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  // Toasts with an action (e.g. Undo) stay longer so there is time to click it
  push: (type, message, duration, action) => {
    const id = ++toastId;
    duration ??= type === 'error' || action ? 8000 : 4000;
    set({ toasts: [...get().toasts, { id, type, message, action }] });
    window.setTimeout(() => get().dismiss(id), duration);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

export const toast = {
  success: (message: string, action?: ToastAction) => useToastStore.getState().push('success', message, undefined, action),
  error: (message: string) => useToastStore.getState().push('error', message),
  warning: (message: string) => useToastStore.getState().push('warning', message),
  info: (message: string) => useToastStore.getState().push('info', message),
};
